<template>
  <div class="feedback-page page-container">
    <section class="page-header">
      <h1>留言板</h1>
      <p>你的每一条建议，都是我们前进的动力</p>
    </section>

    <!-- 非对称两栏：表单 7 / 列表 5 -->
    <section class="feedback-layout">
      <div class="feedback-form-panel">
        <div class="panel-card form-card">
          <h3>提交留言</h3>
          <p class="panel-desc">填写以下信息，我们会认真对待每一份反馈。</p>
          <el-form ref="feedbackForm" :model="form" :rules="rules" label-position="top" class="form-body" @submit.native.prevent>
            <el-form-item label="留言类型" prop="type">
              <el-select v-model="form.type" :disabled="submitting" style="width: 100%">
                <el-option label="功能建议" :value="1" />
                <el-option label="Bug 报错" :value="2" />
                <el-option label="体验优化" :value="3" />
                <el-option label="其他" :value="99" />
              </el-select>
            </el-form-item>
            <el-form-item label="留言内容" prop="content">
              <el-input v-model.trim="form.content" :disabled="submitting" type="textarea" :rows="6" maxlength="2000" show-word-limit />
            </el-form-item>
            <el-form-item label="联系方式（可选）" prop="contact">
              <el-input v-model.trim="form.contact" :disabled="submitting" maxlength="100" placeholder="便于我们回复你" />
            </el-form-item>
          </el-form>
          <div class="form-actions">
            <el-button type="primary" :loading="submitting" icon="el-icon-s-promotion" @click="submit">提交留言</el-button>
            <el-button :disabled="submitting" @click="resetForm">清空</el-button>
          </div>
          <p class="form-tip"><i class="el-icon-time" /> 提交后我们将在 3 个工作日内回复</p>
        </div>
      </div>

      <div class="feedback-list-panel">
        <div class="panel-card list-card">
          <div class="list-header">
            <h3>已处理留言</h3>
            <span class="count-badge">{{ feedbackItems.length }}</span>
          </div>
          <p class="panel-desc">这里展示近期已回复或处理中的反馈。</p>

          <div v-if="loading" v-loading="true" class="loading-state" />
          <el-empty v-else-if="!feedbackItems.length" description="暂无已处理完成的留言" />
          <ul v-else class="feedback-list">
            <li v-for="item in feedbackItems" :key="item.id">
              <div class="item-header">
                <div class="item-meta">
                  <span class="item-type">{{ feedbackTypeText(item.type) }}</span>
                  <time>{{ formatTime(item.createdAt) }}</time>
                </div>
                <span :class="['status-badge', `status-${Number(item.status)}`]">
                  <i :class="statusIcon(item.status)" />{{ statusText(item.status) }}
                </span>
              </div>
              <p class="item-content">{{ item.content }}</p>
            </li>
          </ul>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { listPublicFeedback, submitFeedback } from '../api/feedback'

function emptyForm() {
  return { type: 1, content: '', contact: '' }
}

export default {
  name: 'FeedbackPage',
  data() {
    return {
      feedbackItems: [],
      loading: false,
      submitting: false,
      form: emptyForm(),
      rules: {
        content: [{ required: true, message: '请输入留言内容', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.loadFeedbackList()
  },
  methods: {
    async loadFeedbackList() {
      this.loading = true
      try {
        const { data } = await listPublicFeedback()
        if (data.code !== 200) throw new Error(data.message || '留言加载失败')
        this.feedbackItems = data.data || []
      } catch (error) {
        this.feedbackItems = []
        this.$message.error(this.errorMessage(error, '留言加载失败'))
      } finally {
        this.loading = false
      }
    },
    submit() {
      this.$refs.feedbackForm.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          const { data } = await submitFeedback({ ...this.form, images: '' })
          if (data.code !== 200) throw new Error(data.message || '留言提交失败')
          this.$message.success('留言已提交，处理完成后会展示在留言板中')
          this.resetForm()
          await this.loadFeedbackList()
        } catch (error) {
          this.$message.error(this.errorMessage(error, '留言提交失败'))
        } finally {
          this.submitting = false
        }
      })
    },
    resetForm() {
      this.form = emptyForm()
      if (this.$refs.feedbackForm) this.$refs.feedbackForm.clearValidate()
    },
    feedbackTypeText(type) {
      return { 1: '功能建议', 2: 'Bug 报错', 3: '体验优化', 99: '其他' }[Number(type)] || '留言'
    },
    statusText(status) {
      return { 0: '待处理', 1: '处理中', 2: '已回复', 3: '已忽略' }[Number(status)] || '未知状态'
    },
    statusIcon(status) {
      return { 0: 'el-icon-clock', 1: 'el-icon-loading', 2: 'el-icon-check', 3: 'el-icon-remove-outline' }[Number(status)] || 'el-icon-clock'
    },
    formatTime(value) {
      return value ? String(value).replace('T', ' ') : '时间未知'
    },
    errorMessage(error, fallback) {
      return error.response && error.response.data && error.response.data.message
        ? error.response.data.message : error.message || fallback
    }
  }
}
</script>

<style scoped>
.feedback-page { padding-top: 72px; padding-bottom: 80px; }

.feedback-layout { display: grid; grid-template-columns: repeat(12, 1fr); gap: 32px; margin-top: 40px; }
.feedback-form-panel { grid-column: span 7; }
.feedback-list-panel { grid-column: span 5; }

.panel-card {
  height: 100%;
  padding: 32px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-1);
}
.panel-card h3 { margin: 0; color: var(--ink); font-size: 18px; font-weight: 600; }
.panel-desc { margin: 6px 0 0; color: var(--ink-2); font-size: 13px; }

.form-body { margin-top: 24px; }
.form-actions { display: flex; gap: 12px; margin-top: 8px; }
.form-tip { display: flex; align-items: center; gap: 6px; margin: 18px 0 0; color: var(--ink-2); font-size: 12px; }
.form-tip i { color: var(--ink-3); }

.list-header { display: flex; align-items: center; justify-content: space-between; }
.count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  color: var(--ink-2);
  font-size: 12px;
  font-weight: 600;
  background: var(--surface-2);
  border-radius: 999px;
}
.loading-state { min-height: 240px; }

.feedback-list { margin: 20px 0 0; padding: 0; list-style: none; }
.feedback-list li { padding: 16px 0; border-top: 1px solid var(--line-soft); }
.feedback-list li:first-child { border-top: none; padding-top: 8px; }
.item-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.item-meta { display: flex; align-items: baseline; gap: 8px; }
.item-type { color: var(--ink); font-size: 14px; font-weight: 600; }
.item-meta time { color: var(--ink-3); font-size: 12px; }
.item-content { margin: 10px 0 0; color: var(--ink-2); font-size: 13px; line-height: 1.7; white-space: pre-wrap; overflow-wrap: anywhere; }

/* 状态徽章：已回复(青) / 处理中(琥珀) / 其他(中性) */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  border-radius: 999px;
}
.status-badge i { font-size: 12px; }
.status-0 { color: var(--ink-2); background: var(--surface-2); }
.status-1 { color: var(--state-warning); background: rgba(217, 119, 6, .12); }
.status-2 { color: var(--brand); background: var(--brand-soft); }
.status-3 { color: var(--ink-3); background: var(--surface-2); }

@media (max-width: 900px) {
  .feedback-form-panel, .feedback-list-panel { grid-column: span 12; }
  .feedback-page { padding-top: 40px; padding-bottom: 56px; }
  .panel-card { padding: 24px; }
}
</style>
